import {
  getObjectDefinition,
  type ParamSchema,
} from '../../content/registry/objectRegistry';
import type { MapObjectInstance } from '../../content/schema/map';
import { getDefaultParamSchemas } from './paramSchemas';

export interface ParamIssue {
  instanceId: string;
  assetId: string;
  key: string;
  message: string;
  severity: 'error' | 'warning';
}

export function resolveParamSchema(assetId: string, key: string): ParamSchema | null {
  const definition = getObjectDefinition(assetId);
  return (
    definition?.editor.paramSchema?.[key]
    ?? getDefaultParamSchemas(assetId)?.[key]
    ?? null
  );
}

export function getParamSchemas(assetId: string): Record<string, ParamSchema> {
  return {
    ...getDefaultParamSchemas(assetId),
    ...getObjectDefinition(assetId)?.editor.paramSchema,
  };
}

/** Coerce a candidate value to a schema-valid value, or return null if impossible. */
export function coerceParamValue(
  schema: ParamSchema,
  value: unknown,
): unknown | null {
  switch (schema.type) {
    case 'number': {
      let num = typeof value === 'number' ? value : Number(value);
      if (!Number.isFinite(num)) return null;
      if (schema.integer) num = Math.round(num);
      if (typeof schema.min === 'number') num = Math.max(schema.min, num);
      if (typeof schema.max === 'number') num = Math.min(schema.max, num);
      return Number(num.toFixed(4));
    }
    case 'boolean':
      if (typeof value === 'boolean') return value;
      if (value === 'true' || value === 1) return true;
      if (value === 'false' || value === 0) return false;
      return null;
    case 'string':
      if (typeof value !== 'string') return null;
      if (schema.maxLength && value.length > schema.maxLength) {
        return value.slice(0, schema.maxLength);
      }
      return value;
    case 'enum':
      if (typeof value !== 'string') return null;
      return schema.options.includes(value) ? value : null;
    case 'color':
      if (typeof value !== 'string') return null;
      // Accept #RGB, #RRGGBB, #RRGGBBAA
      return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(value)
        ? value
        : null;
    default:
      return null;
  }
}

/** Validate a single instance and return any schema-driven issues. */
export function validateInstance(instance: MapObjectInstance): ParamIssue[] {
  const issues: ParamIssue[] = [];
  const definition = getObjectDefinition(instance.assetId);
  if (!definition) {
    issues.push({
      instanceId: instance.id,
      assetId: instance.assetId,
      key: '__asset',
      message: `Unknown assetId "${instance.assetId}" - not in registry.`,
      severity: 'error',
    });
    return issues;
  }

  const schemas = getParamSchemas(instance.assetId);
  const params = instance.params ?? {};

  for (const [key, schema] of Object.entries(schemas)) {
    const raw = params[key];
    if (raw === undefined) continue;
    const coerced = coerceParamValue(schema, raw);
    if (coerced === null) {
      issues.push({
        instanceId: instance.id,
        assetId: instance.assetId,
        key,
        message: `Invalid value for ${key} (${schema.type}): ${JSON.stringify(raw)}`,
        severity: 'error',
      });
      continue;
    }
    if (!Object.is(coerced, raw)) {
      issues.push({
        instanceId: instance.id,
        assetId: instance.assetId,
        key,
        message: `Value for ${key} was clamped from ${JSON.stringify(raw)} to ${JSON.stringify(coerced)}.`,
        severity: 'warning',
      });
    }
  }

  return issues;
}

/** Validate an entire snapshot. */
export function validateInstances(instances: MapObjectInstance[]): ParamIssue[] {
  return instances.flatMap((instance) => validateInstance(instance));
}

/** Apply coercion across a snapshot, returning a new snapshot. */
export function normaliseInstances(
  instances: MapObjectInstance[],
): MapObjectInstance[] {
  return instances.map((instance) => {
    const schemas = getParamSchemas(instance.assetId);
    if (!instance.params || Object.keys(schemas).length === 0) return instance;
    const nextParams: Record<string, unknown> = { ...instance.params };
    for (const [key, schema] of Object.entries(schemas)) {
      const raw = nextParams[key];
      if (raw === undefined) continue;
      const coerced = coerceParamValue(schema, raw);
      if (coerced !== null) nextParams[key] = coerced;
    }
    return { ...instance, params: nextParams };
  });
}
